/* eslint-disable jira/integration/test-id-by-folder-structure */

import React, { useState, useEffect, useCallback, type SyntheticEvent } from 'react';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { cssMap } from '@atlaskit/css';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const copyToClipboard = defineMessage({
	id: 'polaris-lib-copy-to-clipboard.copy-to-clipboard',
	defaultMessage: 'Copy to clipboard',
	description:
		'Tooltip text when hovering field name in polaris delete field confirmation dialog to copy the field name into the clipboard',
});

const copiedMessage = defineMessage({
	id: 'polaris-lib-copy-to-clipboard.copied-message',
	defaultMessage: 'Copied',
	description:
		'Tooltip text when clicked on field name in polaris delete field confirmation dialog to copy the field label into the clipboard',
});

const defaultAriaLabel = defineMessage({
	id: 'polaris-lib-copy-to-clipboard.default-aria-label',
	defaultMessage: 'Copy "{text}" to clipboard',
	description: 'Default label for the copy to clipboard button',
});

const styles = cssMap({
	root: {
		font: token('font.body'),
		fontWeight: token('font.weight.semibold'),
		height: '16px',
		display: 'inline-flex',
		alignItems: 'center',
		backgroundColor: 'transparent',
		paddingInline: token('space.0'),
		paddingBlock: token('space.0'),
		'&:hover': {
			cursor: 'pointer',
			textDecoration: 'underline',
		},
	},
});

type CopyToClipboardProps = {
	text: string;
	ariaLabel?: string;
};

export const CopyToClipboard = ({ text, ariaLabel }: CopyToClipboardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isCopied, setCopied] = useState(false);

	const onCopy = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();
			e.stopPropagation();

			globalThis.navigator.clipboard
				.writeText(text)
				.then(() => setCopied(true))
				.catch(() => setCopied(false));
		},
		[text],
	);

	useEffect(() => {
		if (!isCopied) return;
		const copyTimer = setTimeout(() => setCopied(false), 2500);
		return () => clearTimeout(copyTimer);
	}, [isCopied]);

	const defaultAriaLabelText = formatMessage(defaultAriaLabel, { text });
	const ariaLabelText = ariaLabel || defaultAriaLabelText;

	return (
		<Tooltip
			position="top"
			hideTooltipOnMouseDown
			delay={200}
			content={formatMessage(isCopied ? copiedMessage : copyToClipboard)}
		>
			{(tooltipProps) => (
				<Pressable
					{...tooltipProps}
					aria-label={ariaLabelText}
					testId="polaris-lib-copy-to-clipboard.ui.copy-to-clipboard"
					onClick={onCopy}
					xcss={styles.root}
				>
					{text}
				</Pressable>
			)}
		</Tooltip>
	);
};
